import React, { useEffect } from 'react';
import SiteHeader from '../../shared/SiteHeader';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = 'Walk It With Me - Privacy Policy';
  }, []);

  return (
    <>
      <SiteHeader />
      <main className="container py-5">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <h1 className="h2 mb-1">Privacy Policy</h1>
            <p className="text-muted small mb-4">Last updated: May 24, 2026</p>

            <p>
              Walk It With Me ("we," "our," or "us") is committed to protecting your personal
              information. This Privacy Policy explains what data we collect, how we use it, and
              your rights regarding that data.
            </p>

            <section className="mb-4">
              <h2 className="h5">1. Information We Collect</h2>
              <p>We collect the following categories of information when you use our service:</p>
              <ul>
                <li>
                  <strong>Account information</strong> - your email address and password (as a
                  secure hash) when you create an account.
                </li>
                <li>
                  <strong>Profile information</strong> - optional details you provide in your user
                  profile to personalize your experience.
                </li>
                <li>
                  <strong>Chat and devotional content</strong> - the messages you send in chat
                  sessions and your devotional history, so we can save your conversations and
                  generate personalized content.
                </li>
                <li>
                  <strong>Usage data</strong> - page views and feature interactions collected
                  through Azure Application Insights to help us improve the application.
                </li>
              </ul>
            </section>

            <section className="mb-4">
              <h2 className="h5">2. How We Use Your Information</h2>
              <ul>
                <li>To authenticate you and maintain your account.</li>
                <li>To generate personalized AI chat responses and daily devotionals.</li>
                <li>To save your chat history so you can revisit past conversations.</li>
                <li>
                  To monitor application performance and fix bugs using anonymous usage analytics.
                </li>
              </ul>
              <p>We do not use your data for advertising profiling or sell it to third parties.</p>
            </section>

            <section className="mb-4">
              <h2 className="h5">3. Data Storage</h2>
              <p>
                Your data is stored in Microsoft Azure infrastructure located in the United States
                (Central US region). We use Azure Cosmos DB for account and chat data, and Azure
                Blob Storage for application assets. All data is encrypted at rest and in transit.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="h5">4. Third-Party Services</h2>
              <p>We use the following third-party services to operate the application:</p>
              <ul>
                <li>
                  <strong>Microsoft Azure</strong> - cloud hosting, database, and storage.
                </li>
                <li>
                  <strong>Azure Application Insights</strong> - anonymous usage analytics and error
                  monitoring.
                </li>
                <li>
                  <strong>Google AdSense</strong> - advertising. Google may use cookies to serve ads
                  based on your prior visits to this or other websites. You can opt out at{' '}
                  <a
                    href="https://www.google.com/settings/ads"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    google.com/settings/ads
                  </a>
                  .
                </li>
                <li>
                  <strong>AI language model providers</strong> - your chat messages are sent to an
                  AI service to generate responses. Messages are not used to train models.
                </li>
              </ul>
            </section>

            <section className="mb-4">
              <h2 className="h5">5. Data Retention</h2>
              <p>
                We retain your account data and chat history for as long as your account is active.
                If you delete your account, your personal data will be removed within 30 days,
                except where retention is required by law.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="h5">6. Your Rights</h2>
              <p>
                Depending on your location, you may have the right to access, correct, export, or
                delete your personal data. This includes rights under the General Data Protection
                Regulation (GDPR) for users in the European Economic Area, and the California
                Consumer Privacy Act (CCPA) for California residents.
              </p>
              <p>To exercise any of these rights, please contact us at the email address below.</p>
            </section>

            <section className="mb-4">
              <h2 className="h5">7. Cookies</h2>
              <p>
                We use session cookies to keep you logged in. Google AdSense may set additional
                cookies for ad personalization. You can control cookies through your browser
                settings.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="h5">8. Children's Privacy</h2>
              <p>
                Walk It With Me is not directed to children under the age of 13. We do not knowingly
                collect personal information from children under 13.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="h5">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The "Last updated" date at the
                top of this page will reflect any changes. Continued use of the service after
                changes are posted constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="h5">10. Contact Us</h2>
              <p>
                If you have questions or requests regarding your privacy, please contact us at:{' '}
                <a href="mailto:privacy@walkitwithme.com">privacy@walkitwithme.com</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default PrivacyPolicy;
